'use client'

import { AlertCircle } from 'lucide-react'
import { type PropsWithChildren, type ReactNode, useMemo } from 'react'
import {
  LoadingType,
  StepCircle,
} from '@/src/components/route-details/step-circle'
import { StepLine } from '@/src/components/route-details/step-line'
import { cn } from '@/src/utils/styles'

interface TitleProps {
  isError?: boolean
  title: ReactNode
}

export const BasicStepRowTitle = ({ isError, title }: TitleProps) => {
  return (
    <h3
      className={cn('text-secondary', {
        'text-danger-text': isError,
      })}
    >
      {title}
    </h3>
  )
}

interface BasicStepRowProps {
  className?: string
  isActive?: boolean
  isError?: boolean
  isLoading?: boolean
  isSuccess?: boolean
  isWaiting?: boolean
  isWarning?: boolean
  hideBeforeLine?: boolean
  hideAfterLine?: boolean
  iconOnly?: boolean
  icon?: ReactNode
  title: ReactNode
  transferDetails?: {
    startedAtMs: number
    estimatedTimeMs: number
  }
}

export const BasicStepRow = ({
  className,
  isActive,
  isError,
  isLoading,
  isSuccess,
  isWaiting,
  isWarning,
  hideBeforeLine,
  hideAfterLine,
  iconOnly,
  transferDetails,
  icon,
  title,
  children,
}: PropsWithChildren<BasicStepRowProps>) => {
  const loadingType = useMemo(() => {
    if (isWaiting) return LoadingType.Pulse
    if (isLoading && transferDetails) return LoadingType.Progress
    if (isLoading) return LoadingType.Spinner
    return undefined
  }, [isWaiting, isLoading, transferDetails])

  return (
    <li
      className={cn('flex pl-3 gap-5 min-h-10 step-row', className)}
      data-active={isActive}
      data-success={isSuccess}
      data-error={isError}
    >
      <div
        className={cn('flex flex-col items-center flex-grow min-w-6', {
          'pt-6': hideBeforeLine,
          'pb-6': hideAfterLine,
        })}
      >
        {!hideBeforeLine ? (
          <StepLine
            isActive={isActive}
            isSuccess={isSuccess}
            isError={isError}
            className={cn({
              'flex-grow': hideAfterLine,
              'h-3': iconOnly,
            })}
          />
        ) : null}

        {iconOnly || (icon && isLoading === undefined) ? (
          icon
        ) : isWarning ? (
          <AlertCircle width={24} height={24} className='text-danger-text' />
        ) : (
          <div className='relative'>
            <StepCircle
              isSuccess={isSuccess}
              isError={isError}
              isLoading={isError ? false : isLoading}
              type={loadingType}
              transferDetails={transferDetails}
              className={cn('relative z-20 bg-foreground', {
                'min-h-6': !icon,
                'min-h-8 min-w-8 after:min-w-8 after:min-h-8 -mx-1':
                  icon && isLoading !== undefined,
              })}
              svgContainerClassName='bg-foreground'
            />
            {icon ? (
              <div className='absolute inset-0 z-30 flex justify-center items-center'>
                {icon}
              </div>
            ) : null}
          </div>
        )}

        {!hideAfterLine ? (
          <StepLine className='flex-grow step-row-line' />
        ) : null}
      </div>

      <div
        className={cn('flex flex-col gap-1 py-3 w-full', {
          'py-3': hideBeforeLine || hideAfterLine,
          'justify-center': !children,
        })}
      >
        {typeof title === 'string' ? (
          <BasicStepRowTitle isError={isError || isWarning} title={title} />
        ) : (
          title
        )}

        {children ? <div>{children}</div> : null}
      </div>
    </li>
  )
}
